// =============================================================================
// MeuDriver — useEmailNotifications (Batch J1, atualizado Batch K2)
// =============================================================================
// Hook para envio de e-mails transacionais via edge function send-email.
// Usar `useSendEmail()` para ações disparadas pelo usuário (ex: confirmação).
// O envio é sempre best-effort — falhas não bloqueiam o fluxo principal.
//
// Batch K2: adicionado `sendEmailIfAllowed(userId, template, data)` que verifica
// as preferências do usuário antes de enviar — evita spam para quem optou por
// não receber determinada categoria de e-mail.
// =============================================================================

import { useMutation } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

// ---------------------------------------------------------------------------
// Tipos dos templates
// ---------------------------------------------------------------------------

export type EmailTemplate =
  | 'booking_confirmed'
  | 'booking_cancelled_student'
  | 'booking_cancelled_instructor'
  | 'booking_reminder'
  | 'new_review'
  | 'dispute_update'

export interface SendEmailParams {
  to: string
  template: EmailTemplate
  data: Record<string, unknown>
}

// ---------------------------------------------------------------------------
// Fetcher
// ---------------------------------------------------------------------------

async function sendEmail(params: SendEmailParams): Promise<void> {
  const { error } = await supabase.functions.invoke('send-email', {
    body: params,
  })

  if (error) {
    // Loga mas não relança — e-mail é best-effort
    console.warn('[useEmailNotifications] send-email error:', error.message)
  }
}

// ---------------------------------------------------------------------------
// Hooks públicos
// ---------------------------------------------------------------------------

/**
 * Wrapper para chamar a edge function send-email de forma autenticada.
 * Falhas são logadas mas não repassadas ao caller — e-mail é best-effort.
 */
export function useSendEmail() {
  return useMutation<void, Error, SendEmailParams>({
    mutationFn: sendEmail,
    onError: (err) => {
      console.warn('[useSendEmail] falha ao enviar e-mail:', err.message)
    },
  })
}

/**
 * Envia e-mail de forma imperativa (fire-and-forget), sem estado de mutation.
 * Útil para integrar em outros hooks como useBooking e useReviews.
 */
export async function sendEmailSilent(params: SendEmailParams): Promise<void> {
  try {
    await sendEmail(params)
  } catch {
    // silenciado — best-effort
  }
}

// ---------------------------------------------------------------------------
// Mapeamento template → coluna de preferência
// ---------------------------------------------------------------------------

const TEMPLATE_PREFERENCE_KEY: Record<EmailTemplate, string | null> = {
  booking_confirmed:           'email_booking_confirmed',
  booking_cancelled_student:   'email_booking_cancelled',
  booking_cancelled_instructor:'email_booking_cancelled',
  booking_reminder:            'email_booking_reminder',
  new_review:                  'email_new_review',
  dispute_update:              'email_dispute_update',
}

/**
 * Envia e-mail somente se o usuário não tiver desativado a categoria.
 * Se não houver preferências cadastradas (ex: usuário antigo sem registro),
 * envia de qualquer forma (opt-in default).
 *
 * @param userId   UUID do destinatário (para checar preferências)
 * @param template Template do e-mail
 * @param to       Endereço de e-mail do destinatário
 * @param data     Dados do template
 */
export async function sendEmailIfAllowed(
  userId: string,
  template: EmailTemplate,
  to: string,
  data: Record<string, unknown>,
): Promise<void> {
  const prefKey = TEMPLATE_PREFERENCE_KEY[template]

  if (prefKey) {
    try {
      const { data: prefs, error } = await supabase
        .from('notification_preferences')
        .select(prefKey)
        .eq('user_id', userId)
        .maybeSingle()

      if (!error && prefs !== null) {
        const allowed = prefs[prefKey as keyof typeof prefs]
        if (allowed === false) {
          // Usuário optou por não receber esta categoria
          return
        }
      }
      // Se não há registro ou houve erro, envia (opt-in default)
    } catch {
      // Em caso de falha na consulta, envia de qualquer forma
    }
  }

  await sendEmailSilent({ to, template, data })
}
